import { CheckedAddress } from '@sparkdotfi/common-universal'
import { useMemo } from 'react'

import { SavingsAccountSupportedStablecoin } from '../../types'
import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import { Token } from '@/domain/types/Token'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenCell } from '@/ui/molecules/data-table/components/TokenCell'
import { DataTable, DataTableColumnDefinitions } from '@/ui/molecules/data-table/DataTable'
import { ViewContractMoreDropdown } from '@/ui/molecules/view-contract-more-dropdown/ViewContractMoreDropdown'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface EntryAssetsPanelProps {
  assets: SavingsAccountSupportedStablecoin[]
  openDepositDialog: (tokenToDeposit: Token) => void
  openConvertStablesDialog: () => void
  showConvertDialogButton: boolean
  guestMode: boolean
}

export function EntryAssetsPanel({
  assets,
  openDepositDialog,
  openConvertStablesDialog,
  showConvertDialogButton,
  guestMode,
}: EntryAssetsPanelProps) {
  const columnDef: DataTableColumnDefinitions<SavingsAccountSupportedStablecoin> = useMemo(
    () => ({
      token: {
        header: 'Asset',
        renderCell: ({ token }) => <TokenCell token={token} />,
      },
      balance: {
        header: 'Balance',
        headerAlign: 'right',
        renderCell: ({ token, balance }) => (
          <div className="typography-label-2 flex w-full flex-row justify-end">
            {balance.eq(0) ? '-' : token.formatBalance(balance)}
          </div>
        ),
      },
      actions: {
        header: '',
        renderCell: ({ token, balance, blockExplorerLink }) => {
          return (
            <div className="flex justify-end gap-1 sm:gap-3">
              <Button variant="secondary" size="s" disabled={balance.eq(0)} onClick={() => openDepositDialog(token)}>
                Deposit
              </Button>
              {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
                <ViewContractMoreDropdown
                  blockExplorerLink={blockExplorerLink}
                  disabled={token.address === CheckedAddress.EEEE()}
                />
              )}
            </div>
          )
        },
      },
    }),
    [openDepositDialog],
  )

  return (
    <Panel spacing="none">
      <div className={cn('flex flex-col gap-6 p-4 md:px-8 md:py-6', showConvertDialogButton && 'pb-0 md:pb-0')}>
        <h3 className="typography-heading-4 text-primary">Supported assets</h3>
        <DataTable
          gridTemplateColumnsClassName="grid-cols-[repeat(2,1fr)_120px] sm:grid-cols-[repeat(2,1fr)_140px]"
          data={assets}
          columnDef={columnDef}
        />
      </div>
      {showConvertDialogButton && (
        <div
          className="w-full gap-6 border-primary border-t p-4 md:px-8 md:py-6"
          data-testid={testIds.component.ConvertStablesPanel}
        >
          <div className="grid grid-cols-[1fr_auto] items-center gap-3">
            <div className="flex items-center gap-3">
              <img src={tokenLogos.sky} className="h-8 w-8 rounded-full border-2 border-[#DBCAF4]" />
              <div className="flex flex-col">
                <div className="typography-label-3 text-primary">Swap stablecoins without fees and slippage!</div>
                <div className="typography-body-4 text-secondary">
                  Use SKY infrastructure to swap between supported stablecoins.
                </div>
              </div>
            </div>
            <Button
              size="s"
              variant="secondary"
              onClick={openConvertStablesDialog}
              data-testid={testIds.component.ConvertStablesButton}
              disabled={guestMode}
            >
              Swap
            </Button>
          </div>
        </div>
      )}
    </Panel>
  )
}
